import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tabs, Table, Button, DatePicker, Input, Select, Statistic, Icon, Modal, Tag } from 'antd';
import { Chart, Geom, Axis, Tooltip, Legend } from 'bizcharts';
import DataSet from '@antv/data-set';
import moment from 'moment';
import { StyledCard, KPICard, SectionTitle, TagSummaryCard } from './QualityDashboard.styles';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Dummy data: weekly average review score (last 12 weeks)
const weeklyScoreData = [
  { week: 'W1', score: 4.52, scoreYoY: 4.48 },
  { week: 'W2', score: 4.54, scoreYoY: 4.5 },
  { week: 'W3', score: 4.51, scoreYoY: 4.49 },
  { week: 'W4', score: 4.55, scoreYoY: 4.51 },
  { week: 'W5', score: 4.53, scoreYoY: 4.5 },
  { week: 'W6', score: 4.56, scoreYoY: 4.52 },
  { week: 'W7', score: 4.54, scoreYoY: 4.51 },
  { week: 'W8', score: 4.57, scoreYoY: 4.53 },
  { week: 'W9', score: 4.55, scoreYoY: 4.52 },
  { week: 'W10', score: 4.58, scoreYoY: 4.54 },
  { week: 'W11', score: 4.56, scoreYoY: 4.53 },
  { week: 'W12', score: 4.59, scoreYoY: 4.55 }
];

// Dummy: 5-star review rate weekly
const weeklyFiveStarData = [
  { week: 'W1', rate: 0.26, rateYoY: 0.24 },
  { week: 'W2', rate: 0.28, rateYoY: 0.25 },
  { week: 'W3', rate: 0.27, rateYoY: 0.26 },
  { week: 'W4', rate: 0.29, rateYoY: 0.27 },
  { week: 'W5', rate: 0.28, rateYoY: 0.26 },
  { week: 'W6', rate: 0.3, rateYoY: 0.28 },
  { week: 'W7', rate: 0.29, rateYoY: 0.27 },
  { week: 'W8', rate: 0.31, rateYoY: 0.29 },
  { week: 'W9', rate: 0.3, rateYoY: 0.28 },
  { week: 'W10', rate: 0.32, rateYoY: 0.3 },
  { week: 'W11', rate: 0.31, rateYoY: 0.29 },
  { week: 'W12', rate: 0.33, rateYoY: 0.31 }
];

// Quality standards status (count / total)
const qualityStatusList = [
  { label: 'Good', count: 200, total: 410, color: '#52c41a' },
  { label: 'Educate', count: 100, total: 410, color: '#faad14' },
  { label: 'Warn', count: 30, total: 410, color: '#faad14' },
  { label: 'Probation', count: 30, total: 410, color: '#faad14' },
  { label: 'Additional warn', count: 20, total: 410, color: '#ff4d4f' },
  { label: 'Pending removal', count: 10, total: 410, color: '#ff4d4f' },
  { label: 'Suspended', count: 10, total: 410, color: '#ff4d4f' },
  { label: 'Removed', count: 10, total: 410, color: '#ff4d4f' }
];

// Review Tag Summary: category cards with tags and percentages (dummy data)
const reviewTagSummary = [
  {
    key: 'accuracy',
    name: 'Accuracy',
    icon: 'file-text',
    reviewCount: 108,
    tags: [
      { label: 'Looked like the photos', percent: 21 },
      { label: 'Matched the description', percent: 20 },
      { label: 'Had listed amenities & services', percent: 16 }
    ]
  },
  {
    key: 'cleanliness',
    name: 'Cleanliness',
    icon: 'home',
    reviewCount: 108,
    tags: [
      { label: 'Spotless furniture & linens', percent: 21 },
      { label: 'Pristine kitchen', percent: 20 },
      { label: 'Squeaky-clean bathroom', percent: 20 },
      { label: 'Free of clutter', percent: 17 }
    ]
  },
  {
    key: 'checkin',
    name: 'Check-in',
    icon: 'login',
    reviewCount: 108,
    tags: [
      { label: 'Clear instructions', percent: 22 },
      { label: 'Easy to get inside', percent: 17 },
      { label: 'Felt right at home', percent: 15 },
      { label: 'Responsive Host', percent: 15 },
      { label: 'Easy to find', percent: 14 }
    ]
  },
  {
    key: 'communication',
    name: 'Communication',
    icon: 'message',
    reviewCount: 108,
    tags: [
      { label: 'Considerate', percent: 21 },
      { label: 'Always responsive', percent: 18 },
      { label: 'Helpful instructions', percent: 17 },
      { label: 'Proactive', percent: 6 }
    ]
  },
  {
    key: 'location',
    name: 'Location',
    icon: 'environment',
    reviewCount: 108,
    tags: [
      { label: 'Peaceful', percent: 21 },
      { label: 'Beautiful surroundings', percent: 20 },
      { label: 'Private', percent: 13 },
      { label: 'Walkable', percent: 4 }
    ]
  }
];

// Category KPIs (Cleanliness, Accuracy, etc.) with WoW/YoY
const categoryKpis = [
  { name: 'Cleanliness', value: 4.56, wow: 0.02, yoy: 0.04, positive: true },
  { name: 'Accuracy', value: 4.7, wow: 0.02, yoy: 0.04, positive: true },
  { name: 'Communication', value: 4.6, wow: -0.02, yoy: -0.04, positive: false },
  { name: 'Check in', value: 4.7, wow: 0.02, yoy: 0.04, positive: true },
  { name: 'Location', value: 4.85, wow: 0.02, yoy: 0.04, positive: true },
  { name: 'Value', value: 4.4, wow: -0.02, yoy: -0.04, positive: false }
];

// Detailed reviews table dummy data
const detailedReviewsData = [
  {
    key: '1',
    dateOfReception: '9-Apr-2024',
    hostIdName: '123 Mister X',
    listingIdName: 'Property Ma Beautiful Sea side house 12',
    airbnbResId: 'HMHWNBX8NP',
    guestName: 'Guiseppe',
    publicReview: 'I loved this place. Thank you for being so welcoming.',
    privateReview: '',
    overallRating: 5,
    locationRating: 5,
    commRating: 5,
    valueRating: 5,
    checkinRating: 5,
    accuracyRating: 5,
    cleanlinessRating: 5,
    negativeTags: 'Dirty Bathroom',
    qualityStatus: 'Good',
    hostReply: ''
  },
  {
    key: '2',
    dateOfReception: '10-Apr-2024',
    hostIdName: '456 Host B',
    listingIdName: 'Seaside View Apartment',
    airbnbResId: 'HAB2CD3E4F',
    guestName: 'Maria',
    publicReview: 'Great location and clean.',
    privateReview: 'Check-in instructions could be clearer.',
    overallRating: 4,
    locationRating: 5,
    commRating: 4,
    valueRating: 4,
    checkinRating: 4,
    accuracyRating: 5,
    cleanlinessRating: 5,
    negativeTags: 'Unclear Instructions',
    qualityStatus: 'Educate',
    hostReply: ''
  },
  {
    key: '3',
    dateOfReception: '11-Apr-2024',
    hostIdName: '789 Host C',
    listingIdName: 'City Central Studio',
    airbnbResId: 'HXY9ZKW7MN',
    guestName: 'John',
    publicReview: 'Good value for money.',
    privateReview: '',
    overallRating: 5,
    locationRating: 5,
    commRating: 5,
    valueRating: 5,
    checkinRating: 5,
    accuracyRating: 5,
    cleanlinessRating: 4,
    negativeTags: '',
    qualityStatus: 'Good',
    hostReply: "Thank you for your kind words! We're glad you enjoyed your stay."
  }
];

const QualityDashboardDemo1 = () => {
  const [activeTab, setActiveTab] = useState('listings');
  const [dateRange, setDateRange] = useState(null);
  const [hostFilter, setHostFilter] = useState('');
  const [listingFilter, setListingFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [qualityStatusFilter, setQualityStatusFilter] = useState('ALL');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [reviewViewMode, setReviewViewMode] = useState('per_reservation');
  const [replyModalVisible, setReplyModalVisible] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [hostReplyText, setHostReplyText] = useState('');
  const [hostReplies, setHostReplies] = useState({});

  const clearFilters = () => {
    setDateRange(null);
    setHostFilter('');
    setListingFilter('');
    setCountryFilter('');
    setCityFilter('');
    setQualityStatusFilter('ALL');
  };

  const removeFilter = key => {
    if (key === 'dateRange') setDateRange(null);
    if (key === 'host') setHostFilter('');
    if (key === 'listing') setListingFilter('');
    if (key === 'country') setCountryFilter('');
    if (key === 'city') setCityFilter('');
    if (key === 'qualityStatus') setQualityStatusFilter('ALL');
  };

  const activeFilterTags = [];
  if (dateRange && dateRange[0] && dateRange[1]) {
    activeFilterTags.push({
      key: 'dateRange',
      label: `From: ${moment(dateRange[0]).format('YYYY-MM-DD')} To: ${moment(dateRange[1]).format('YYYY-MM-DD')}`
    });
  }
  if (qualityStatusFilter && qualityStatusFilter !== 'ALL') {
    activeFilterTags.push({ key: 'qualityStatus', label: `Status: ${qualityStatusFilter}` });
  }
  if (hostFilter.trim()) activeFilterTags.push({ key: 'host', label: `Host: ${hostFilter.trim()}` });
  if (listingFilter.trim()) activeFilterTags.push({ key: 'listing', label: `Listing: ${listingFilter.trim()}` });
  if (countryFilter.trim()) activeFilterTags.push({ key: 'country', label: `Country: ${countryFilter.trim()}` });
  if (cityFilter.trim()) activeFilterTags.push({ key: 'city', label: `City: ${cityFilter.trim()}` });
  const hasActiveFilters = activeFilterTags.length > 0;

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [activeTab]);

  // Transform weekly score for BizCharts (fold Current vs YoY)
  const dsScore = new DataSet();
  const dvScore = dsScore.createView().source(weeklyScoreData);
  dvScore
    .transform({
      type: 'fold',
      fields: ['score', 'scoreYoY'],
      key: 'type',
      value: 'value'
    })
    .transform({
      type: 'map',
      callback: row => ({
        ...row,
        type: row.type === 'score' ? 'Current' : 'YoY'
      })
    });

  const dsFiveStar = new DataSet();
  const dvFiveStar = dsFiveStar.createView().source(weeklyFiveStarData);
  dvFiveStar
    .transform({
      type: 'fold',
      fields: ['rate', 'rateYoY'],
      key: 'type',
      value: 'value'
    })
    .transform({
      type: 'map',
      callback: row => ({
        ...row,
        type: row.type === 'rate' ? 'Current' : 'YoY',
        value: Number((row.value * 100).toFixed(1))
      })
    });

  const detailedReviewsColumns = [
    { title: 'Date of Reception', dataIndex: 'dateOfReception', key: 'dateOfReception', width: 110 },
    { title: 'Host ID / Name', dataIndex: 'hostIdName', key: 'hostIdName', width: 120 },
    { title: 'Listing ID / Name', dataIndex: 'listingIdName', key: 'listingIdName', width: 200 },
    { title: 'Airbnb Reservation ID', dataIndex: 'airbnbResId', key: 'airbnbResId', width: 130 },
    { title: 'Guest Name', dataIndex: 'guestName', key: 'guestName', width: 100 },
    { title: 'Public Review', dataIndex: 'publicReview', key: 'publicReview', ellipsis: true },
    { title: 'Private Review', dataIndex: 'privateReview', key: 'privateReview', ellipsis: true },
    {
      title: 'Overall Rating',
      dataIndex: 'overallRating',
      key: 'overallRating',
      width: 100,
      render: val => (
        <span style={{ backgroundColor: val >= 4.7 ? '#f6ffed' : val >= 4.6 ? '#fff7e6' : '#fff2f0', padding: '2px 8px', borderRadius: 4 }}>
          {val}
        </span>
      )
    },
    { title: 'Location', dataIndex: 'locationRating', key: 'locationRating', width: 80 },
    { title: 'Comm', dataIndex: 'commRating', key: 'commRating', width: 80 },
    { title: 'Value', dataIndex: 'valueRating', key: 'valueRating', width: 80 },
    { title: 'Check in', dataIndex: 'checkinRating', key: 'checkinRating', width: 90 },
    { title: 'Accuracy', dataIndex: 'accuracyRating', key: 'accuracyRating', width: 90 },
    { title: 'Cleanliness', dataIndex: 'cleanlinessRating', key: 'cleanlinessRating', width: 100 },
    { title: 'Negative Tags', dataIndex: 'negativeTags', key: 'negativeTags', width: 130 },
    { title: 'Quality Standard Status', dataIndex: 'qualityStatus', key: 'qualityStatus', width: 160 },
    {
      title: 'Action',
      key: 'reply',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const reply = hostReplies[record.key] !== undefined ? hostReplies[record.key] : record.hostReply;
        const hasReply = reply && String(reply).trim();
        return (
          <Button
            type="link"
            size="small"
            onClick={e => {
              e.stopPropagation();
              setSelectedReview(record);
              setHostReplyText(hasReply ? String(reply) : '');
              setReplyModalVisible(true);
            }}
          >
            {hasReply ? 'View / Edit reply' : 'Reply'}
          </Button>
        );
      }
    }
  ];

  const handleDownloadReport = () => {
    const headers = detailedReviewsColumns.map(c => c.title).join(',');
    const rows = detailedReviewsData.map(r =>
      [
        r.dateOfReception,
        r.hostIdName,
        `"${(r.listingIdName || '').replace(/"/g, '""')}"`,
        r.airbnbResId,
        r.guestName,
        `"${(r.publicReview || '').replace(/"/g, '""')}"`,
        `"${(r.privateReview || '').replace(/"/g, '""')}"`,
        r.overallRating,
        r.locationRating,
        r.commRating,
        r.valueRating,
        r.checkinRating,
        r.accuracyRating,
        r.cleanlinessRating,
        r.negativeTags,
        r.qualityStatus
      ].join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `quality_detailed_reviews_${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <StyledCard
      title={
        <span>
          Airbnb Quality Dashboard (Demo 1 — Airbnb Mock Up){' '}
          <a
            href="https://docs.google.com/spreadsheets/d/1vCTdy0zdtL3F5zRLfukjQg0tBJ_-n5VHDDfGAVNJEs0/edit?gid=0#gid=0"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}
          >
            Reference Link - Airbnb Mock Up
          </a>
        </span>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Listings Quality" key="listings">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
              padding: '12px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <Button icon="filter" onClick={() => setFiltersVisible(true)}>
              Filters
              {hasActiveFilters && (
                <Tag color="blue" style={{ marginLeft: 6 }}>
                  {activeFilterTags.length}
                </Tag>
              )}
            </Button>
            {activeFilterTags.map(({ key, label }) => (
              <Tag key={key} closable onClose={() => removeFilter(key)} color="blue">
                {label}
              </Tag>
            ))}
            {hasActiveFilters && (
              <a onClick={clearFilters} style={{ marginLeft: 'auto', fontSize: 13 }}>
                Clear filters
              </a>
            )}
          </div>

          <Row gutter={24}>
            <Col xs={24}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <SectionTitle style={{ marginBottom: 0 }}>Metrics</SectionTitle>
                <Button type="primary" icon="download" onClick={handleDownloadReport}>
                  Download CSV
                </Button>
              </div>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <KPICard>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Airbnb Overall AV. REVIEW SCORE</div>
                    <Statistic value={4.7} valueStyle={{ color: '#52c41a', fontSize: 28 }} />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      <span style={{ color: '#52c41a' }}>+0.02 WoW</span> <span style={{ color: '#52c41a' }}>+0.04 YoY</span>
                    </div>
                  </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <KPICard>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>5-Star Review Rate</div>
                    <Statistic value="30%" valueStyle={{ color: '#fa8c16', fontSize: 28 }} />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      <span style={{ color: '#52c41a' }}>+0.3% WoW</span> <span style={{ color: '#52c41a' }}>+15.2% YoY</span>
                    </div>
                  </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <KPICard>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Top 3 Negative Tags</div>
                    <div style={{ fontSize: 12 }}>
                      <div>1. Cleanliness – Dirty Bathroom (30%)</div>
                      <div>2. Checkin – Unclear Instructions (25%)</div>
                      <div>3. Communication – Slow to respond (15%)</div>
                    </div>
                  </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <KPICard>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Listings in "Good or Education"</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <Statistic value="230/410" valueStyle={{ color: '#52c41a', fontSize: 22 }} />
                      <Icon type="arrow-up" style={{ color: '#52c41a', fontSize: 14 }} />
                    </div>
                  </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <KPICard>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Listings in "Warning"</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <Statistic value="40/410" valueStyle={{ color: '#fa8c16', fontSize: 22 }} />
                      <Icon type="arrow-up" style={{ color: '#fa8c16', fontSize: 14 }} />
                    </div>
                    <Button type="link" size="small" style={{ padding: 0 }}>
                      View listings
                    </Button>
                  </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <KPICard>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>At risk of Suspension / Removals</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <Statistic value="30/410" valueStyle={{ color: '#ff4d4f', fontSize: 22 }} />
                      <Icon type="arrow-up" style={{ color: '#ff4d4f', fontSize: 14 }} />
                    </div>
                    <Button type="link" size="small" style={{ padding: 0 }}>
                      View listings
                    </Button>
                  </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <KPICard>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Listings REMOVED</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <Statistic value="10/410" valueStyle={{ color: '#1890ff', fontSize: 22 }} />
                      <Icon type="arrow-up" style={{ color: '#1890ff', fontSize: 14 }} />
                    </div>
                  </KPICard>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} lg={16}>
                  <Card title="Weekly Av. Review Score with YoY Comparison" size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                    <Chart height={280} data={dvScore} forceFit>
                      <Legend />
                      <Axis name="week" />
                      <Axis name="value" />
                      <Tooltip />
                      <Geom type="line" position="week*value" color="type" size={2} shape="smooth" />
                      <Geom type="point" position="week*value" color="type" size={3} shape="circle" />
                    </Chart>
                  </Card>
                  <Card title="Weekly 5-Star Review Rate with YoY Comparison" size="small" style={{ borderRadius: 8 }}>
                    <Chart height={280} data={dvFiveStar} forceFit>
                      <Legend />
                      <Axis name="week" />
                      <Axis name="value" label={{ formatter: v => `${v}%` }} />
                      <Tooltip formatter={v => `${v}%`} />
                      <Geom type="line" position="week*value" color="type" size={2} shape="smooth" />
                      <Geom type="point" position="week*value" color="type" size={3} shape="circle" />
                    </Chart>
                  </Card>
                </Col>
                <Col xs={24} lg={8}>
                  <Card title="Notes" size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ marginBottom: 8 }}>
                        <strong>5-star metrics:</strong>
                      </div>
                      <div>≥ 4.7 → Green</div>
                      <div>4.6 – &lt;4.7 → Orange</div>
                      <div>&lt; 4.6 → Red</div>
                      <div style={{ marginTop: 12 }}>
                        <strong>5-Star Review Rate:</strong>
                      </div>
                      <div>≥ 40% → Green</div>
                      <div>30% – 40% → Orange</div>
                      <div>&lt; 30% → Red</div>
                    </div>
                  </Card>
                  <Card title="Quality Standards Status" size="small" style={{ borderRadius: 8 }}>
                    {qualityStatusList.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          marginBottom: 4,
                          border: '1px solid #e8e8e8',
                          borderRadius: 6,
                          fontSize: 13
                        }}
                      >
                        <span>
                          <strong>{item.count}</strong> / {item.total}
                        </span>
                        <span style={{ color: item.color, fontWeight: 600 }}>{item.label}</span>
                      </div>
                    ))}
                  </Card>
                </Col>
              </Row>

              <SectionTitle style={{ marginTop: 24 }}>Review Tag Summary</SectionTitle>
              <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
                When guests leave reviews, they rate Accuracy, Cleanliness, Check-in, Communication, and Location. In each card you see the number of
                reviews for that category and the most frequent tags with their share.
              </p>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {reviewTagSummary.map(cat => (
                  <Col xs={24} sm={12} md={8} key={cat.key}>
                    <TagSummaryCard size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>
                          <Icon type={cat.icon} style={{ marginRight: 8 }} />
                          {cat.name}
                        </span>
                        <span style={{ fontSize: 12, color: '#666' }}>reviews: {cat.reviewCount}</span>
                      </div>
                      <div>
                        {cat.tags.map((tag, idx) => (
                          <div key={idx} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                              <span>{tag.label}</span>
                              <span>{tag.percent}%</span>
                            </div>
                            <div
                              style={{
                                height: 6,
                                borderRadius: 3,
                                background: '#e8e8e8',
                                overflow: 'hidden'
                              }}
                            >
                              <div
                                style={{
                                  height: '100%',
                                  width: `${tag.percent}%`,
                                  background: '#b7eb8f',
                                  borderRadius: 3
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </TagSummaryCard>
                  </Col>
                ))}
              </Row>

              <SectionTitle style={{ marginTop: 24 }}>Weekly Av. Review Score by Category (last 12 weeks)</SectionTitle>
              <Row gutter={[16, 16]}>
                {categoryKpis.map((cat, idx) => (
                  <Col xs={24} sm={12} md={8} key={idx}>
                    <KPICard>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{cat.name}</div>
                      <Statistic value={cat.value} valueStyle={{ color: cat.positive ? '#52c41a' : '#ff4d4f', fontSize: 24 }} />
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        <span style={{ color: cat.wow >= 0 ? '#52c41a' : '#ff4d4f' }}>
                          {cat.wow >= 0 ? '+' : ''}
                          {cat.wow} WoW
                        </span>{' '}
                        <span style={{ color: cat.yoy >= 0 ? '#52c41a' : '#ff4d4f' }}>
                          {cat.yoy >= 0 ? '+' : ''}
                          {cat.yoy} YoY
                        </span>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                        <div>Positive: Extremely clean and tidy (25%), Lorem ipsum (xx%)</div>
                        <div style={{ color: '#ff4d4f' }}>Negative: Dirty Bathroom (25%), Lorem ipsum (xx%)</div>
                      </div>
                    </KPICard>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Reviews Quality" key="reviews">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
              padding: '12px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <Button icon="filter" onClick={() => setFiltersVisible(true)}>
              Filters
              {hasActiveFilters && (
                <Tag color="blue" style={{ marginLeft: 6 }}>
                  {activeFilterTags.length}
                </Tag>
              )}
            </Button>
            {activeFilterTags.map(({ key, label }) => (
              <Tag key={key} closable onClose={() => removeFilter(key)} color="blue">
                {label}
              </Tag>
            ))}
            {hasActiveFilters && (
              <a onClick={clearFilters} style={{ marginLeft: 'auto', fontSize: 13 }}>
                Clear filters
              </a>
            )}
          </div>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={8}>
              <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Dirty bathroom</div>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#d46b08' }}>30%</div>
                <div style={{ fontSize: 12, color: '#666' }}>of total reviews</div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Too expensive</div>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#d46b08' }}>25%</div>
                <div style={{ fontSize: 12, color: '#666' }}>of total reviews</div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Slow to respond</div>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#d46b08' }}>15%</div>
                <div style={{ fontSize: 12, color: '#666' }}>of total reviews</div>
              </Card>
            </Col>
          </Row>

          <Card size="small" style={{ marginBottom: 16 }}>
            <Chart height={220} data={dvScore} forceFit>
              <Legend />
              <Axis name="week" />
              <Axis name="value" />
              <Tooltip />
              <Geom type="line" position="week*value" color="type" size={2} shape="smooth" />
            </Chart>
          </Card>
          <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
            Reviews are per booking. Guests leave a review after their stay; you can respond to each review below.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <SectionTitle style={{ marginBottom: 0 }}>Reviews</SectionTitle>
              <Button type="primary" size="small">
                Respond to reviews
              </Button>
              <Button.Group size="small">
                <Button type={reviewViewMode === 'per_reservation' ? 'primary' : 'default'} onClick={() => setReviewViewMode('per_reservation')}>
                  Per reservation
                </Button>
                <Button type={reviewViewMode === 'per_listing' ? 'primary' : 'default'} onClick={() => setReviewViewMode('per_listing')}>
                  Per listing
                </Button>
              </Button.Group>
            </div>
            <Button type="primary" icon="download" onClick={handleDownloadReport}>
              Download report
            </Button>
          </div>
          <Table columns={detailedReviewsColumns} dataSource={detailedReviewsData} scroll={{ x: 1600 }} pagination={{ pageSize: 10 }} size="small" />
        </Tabs.TabPane>
      </Tabs>

      <Modal
        visible={replyModalVisible}
        title="Respond to review"
        width={560}
        onCancel={() => {
          setReplyModalVisible(false);
          setSelectedReview(null);
          setHostReplyText('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setReplyModalVisible(false);
              setSelectedReview(null);
              setHostReplyText('');
            }}
          >
            Cancel
          </Button>,
          <Button
            key="reply"
            type="primary"
            onClick={() => {
              if (selectedReview) {
                setHostReplies(prev => ({ ...prev, [selectedReview.key]: hostReplyText }));
              }
              setReplyModalVisible(false);
              setSelectedReview(null);
              setHostReplyText('');
            }}
          >
            Reply
          </Button>
        ]}
      >
        {selectedReview && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Guest</div>
              <div style={{ fontWeight: 600 }}>{selectedReview.guestName}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {selectedReview.listingIdName} · {selectedReview.airbnbResId}
              </div>
            </div>
            <div style={{ marginBottom: 12, fontSize: 12 }}>
              <span style={{ color: '#666' }}>Overall: </span>
              <strong>{selectedReview.overallRating}/5</strong>
              <span style={{ color: '#666', marginLeft: 12 }}>
                Cleanliness {selectedReview.cleanlinessRating} · Accuracy {selectedReview.accuracyRating} · Check-in {selectedReview.checkinRating} ·
                Communication {selectedReview.commRating} · Location {selectedReview.locationRating} · Value {selectedReview.valueRating}
              </span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Public review (guest)</div>
              <div style={{ padding: 12, background: '#fafafa', borderRadius: 6, fontSize: 13 }}>{selectedReview.publicReview || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Your reply</div>
              <Input.TextArea
                rows={4}
                value={hostReplyText}
                onChange={e => setHostReplyText(e.target.value)}
                placeholder="Write your response to the guest's review..."
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        visible={filtersVisible}
        title="Filters"
        onCancel={() => setFiltersVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <Button style={{ marginRight: 8 }} onClick={() => setFiltersVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" onClick={() => setFiltersVisible(false)}>
                Apply
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Date Range</div>
          <RangePicker value={dateRange} onChange={setDateRange} style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Host name / ID</div>
          <Input placeholder="e.g. Property Manager A" value={hostFilter} onChange={e => setHostFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Listing name / ID</div>
          <Input placeholder="e.g. Seaside View Apartment" value={listingFilter} onChange={e => setListingFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Country</div>
          <Input placeholder="e.g. Italy" value={countryFilter} onChange={e => setCountryFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>City</div>
          <Input placeholder="e.g. Napoli" value={cityFilter} onChange={e => setCityFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Latest Quality Status</div>
          <Select value={qualityStatusFilter} onChange={setQualityStatusFilter} style={{ width: '100%' }}>
            <Option value="ALL">ALL</Option>
            <Option value="Good">Good</Option>
            <Option value="Educate">Educate</Option>
            <Option value="Warn">Warn</Option>
            <Option value="Probation">Probation</Option>
            <Option value="Removed">Removed</Option>
          </Select>
        </div>
      </Modal>
    </StyledCard>
  );
};

export default QualityDashboardDemo1;
